import { AxiosError } from 'axios';
import api from './api';

export type AdapterMode = 'ingestion' | 'passthrough' | 'federated';
export type PulseRollup = 'active' | 'eligible' | 'ineligible';

export interface OverviewAccount {
  id: string;
  name: string | null;
  type: string | null;
  businessTimezone: string | null;
  recordsToday: number;
}

export interface OverviewSource {
  id: string;
  name: string;
  sourceType: string;
  integrationId: string;
  mode: AdapterMode;
  syncEnabled: boolean;
  aiAccessEnabled: boolean;
  healthStatus: string;
  syncStatus: string | null;
  lastSyncAt: string | null;
  nextSyncAt: string | null;
  isActive: boolean;
  /** Only 'plan_limit' is ever surfaced — a source switched off by the billing
   *  over-limit sweep. Its data is preserved and it can be turned back on. */
  deactivationReason: string | null;
  accounts: OverviewAccount[];
  counts: {
    queries30d: number;
    recordsToday: number;
    /** null when the caller may not see grant counts (admin-only — D34). */
    people: number | null;
    semantics: number;
    routines: number;
  };
  pulse: PulseRollup;
  wiki: { exists: boolean; pageId: string | null; updatedAt: string | null };
}

export interface OverviewOrganization {
  id: string;
  name: string;
  sources: OverviewSource[];
}

export interface SourcesOverview {
  organizations: OverviewOrganization[];
  totals: {
    /** Active sources only — what the workspace is billed on. */
    sources: number;
    /** Sources switched off by the billing over-limit sweep. */
    inactive: number;
    queries30d: number;
    recordsToday: number;
    needsAttention: number;
  };
}

export interface PickerAccount {
  id: string;
  name: string | null;
}

export interface PickerSource {
  id: string;
  name: string;
  mode: AdapterMode;
  /** Owning business — a grouping label for the viewer's filter, not a scope. */
  organizationId: string;
  organizationName: string;
  accounts: PickerAccount[];
}

export interface AccessibleAccounts {
  sources: PickerSource[];
}

/** What deleting a source destroys — shown in the delete confirm (P1). */
export interface SourceDeletionImpact {
  records: number;
  wikiPages: number;
  semanticEntries: number;
  usersLosingAccess: number;
  queries30d: number;
}

export const sourcesApi = {
  /** The Data sources page aggregate — grant-filtered, workspace-wide, grouped by org. */
  getOverview: async (): Promise<SourcesOverview> => {
    const { data } = await api.get('/api/sources/overview');
    return data.data as SourcesOverview;
  },

  /** Lightweight account list for the Data viewer picker — no per-source metrics. */
  getAccessibleAccounts: async (): Promise<AccessibleAccounts> => {
    const { data } = await api.get('/api/sources/accessible-accounts');
    return data.data as AccessibleAccounts;
  },

  /** Partial update: rename, pause/resume syncing, pause/resume AI access. Admin-only. */
  updateSource: async (
    sourceId: string,
    changes: { name?: string; syncEnabled?: boolean; aiAccessEnabled?: boolean },
  ): Promise<void> => {
    await api.put(`/api/integrations/data-sources/${sourceId}`, changes);
  },

  deleteSource: async (sourceId: string): Promise<void> => {
    await api.delete(`/api/integrations/data-sources/${sourceId}`);
  },

  /** Counts shown in the delete confirm (records purged, people losing access, etc.). Admin-only. */
  getDeletionImpact: async (sourceId: string): Promise<SourceDeletionImpact> => {
    const { data } = await api.get(`/api/sources/${sourceId}/deletion-impact`);
    return data.data as SourceDeletionImpact;
  },

  /**
   * Turn a plan-limit-disabled source back on. Admin-only. Rejects with 402
   * ADDON_REQUIRED / 403 UPGRADE_REQUIRED when the workspace has no free source
   * slot — the caller sends the user to billing. Silenced so the page owns that
   * UX instead of the interceptor firing a second, generic toast.
   */
  reactivateSource: async (sourceId: string): Promise<void> => {
    await api.post(`/api/sources/${sourceId}/reactivate`, undefined, { silenceToast: true });
  },

  updateAccountTimezone: async (accountId: string, businessTimezone: string): Promise<void> => {
    await api.put(`/api/integrations/accounts/${accountId}/settings`, {
      business_timezone: businessTimezone,
    });
  },
};

/** The workspace's source pool is full — a plan change is the only way forward. */
export const isPlanCapacityError = (error: unknown): boolean => {
  const code = (error as AxiosError<{ code?: string }>)?.response?.data?.code;
  return code === 'ADDON_REQUIRED' || code === 'UPGRADE_REQUIRED';
};
